/**
 * vaccinationApiService.js
 *
 * The authoritative vaccination API service for the entire UWIN vaccination
 * module.  This file is the ONLY place that owns:
 *
 *   1. encryptText - the async helper that calls the backend encryption endpoint
 *   2. createVaccinationRecord - assembles the payload and calls the API
 *
 * PREVIOUS VIOLATION FIXED
 * ------------------------
 * createVaccinationRecord used to live inside memberVaccination/MemberVaccination.jsx
 * (a UI component file) and was exported from there.  VaccinationContainer.jsx then
 * crossed module boundaries to import it:
 *
 *   import { createVaccinationRecord } from "../memberVaccination/MemberVaccination";
 *
 * That created a hard dependency from VaccinationModule on a sibling component,
 * which makes the modules impossible to understand or test in isolation.
 *
 * CORRECT IMPORT PATH FOR ALL CONSUMERS
 * --------------------------------------
 * VaccinationContainer.jsx  -> import from "./services/vaccinationApiService"
 * MemberVaccination.jsx     -> import from "../VaccinationModule/services/vaccinationApiService"
 * VaccinationReviewCard.jsx -> import from "./VaccinationModule/services/vaccinationApiService"
 * VaccinationRejectionCard  -> import from "./VaccinationModule/services/vaccinationApiService"
 *
 * MATERIAL DETAILS CONTRACT
 * -------------------------
 * The three materialDetails builders are imported from the existing
 * memberVaccination/materialDetailsService.js.  That file is a pure-function
 * library with no component dependencies, so it is safe to import from here.
 *
 * LOCATION HELPER CONTRACT
 * ------------------------
 * getVillageName / getHealthVillageName / getWardName are imported from the
 * VaccinationModule-internal vaccinationUtils layer.  They are pure lookup
 * functions with no side effects.
 */

import { uwinAPI } from '../../../../../services/uwin.api';
import {
  buildRejectionMaterialDetails,
  buildCurrentMaterialDetails,
  buildPreviousMaterialDetails,
} from '../../memberVaccination/materialDetailsService';
import {
  getVillageName,
  getHealthVillageName,
  getWardName,
  getHealthWardName,
} from '../vaccinationUtils';

// ─────────────────────────────────────────────────────────────────────────────
// ENCRYPTION HELPER
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Encrypt a plaintext string via the backend encryption endpoint.
 *
 * Keeping this as a module-level function (not a closure inside
 * createVaccinationRecord) means it can be shared with any future service
 * in this module without duplication.
 *
 * @param {string} plaintext
 * @returns {Promise<string>} encrypted ciphertext
 */
export const encryptText = async (plaintext) => {
  try {
    const res = await uwinAPI.encryptText({ plaintext });
    return res.data.encrypted;
  } catch (error) {
    console.error('encryptText error:', error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// CENTRALIZED VACCINATION API SERVICE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * createVaccinationRecord
 *
 * Assembles the full API payload for a vaccination event (current, previous,
 * or rejection) and submits it via uwinAPI.createVaccinationRecord.
 *
 * This function intentionally knows nothing about React or component state.
 * All data it needs arrives as plain arguments, keeping it fully testable.
 *
 * @param {Object}   formData           - Formik values enriched with metadata
 * @param {string}   vaccinationType    - 'current' | 'previous' | 'rejection'
 * @param {Object}   [rejectionData]    - Only required for 'rejection' type
 * @param {Object}   [beneficiaryData]  - Beneficiary record from session
 * @param {Array}    [villages]         - Transformed village list
 * @param {Array}    [healthVillages]   - Transformed health village list
 * @param {Array}    [wards]            - Transformed ward list
 * @param {Array}    [healthWards]      - Transformed health ward list
 * @returns {Promise<{ success: boolean, data: any, message: string, payload: Object }>}
 */
export const createVaccinationRecord = async (
  formData,
  vaccinationType = 'current',
  rejectionData = null,
  beneficiaryData = null,
  villages = [],
  healthVillages = [],
  wards = [],
  healthWards = []
) => {
  try {
    console.log('vaccinationApiService.createVaccinationRecord called:', {
      formData,
      vaccinationType,
      rejectionData,
    });

    // Base payload - required fields for every scenario
    const payload = {
      session_id: beneficiaryData?.session_id || '',
      beneficiary_id: beneficiaryData?.beneficiary_id || '',
    };

    // session_site_id rules:
    //   rejection → always sent (session context is mandatory for rejection records)
    //   current   → always sent (mandatory site context for every current vaccination)
    //   previous  → never sent
    if (vaccinationType === 'rejection' || vaccinationType === 'current') {
      payload.session_site_id = beneficiaryData?.session_site_id || '';
    }

    // Location dropdown fields and session_site_id are mutually exclusive for
    // current vaccination:
    //   - tag_to_session_site = true  → send session_site_id only (already added above)
    //   - tag_to_session_site = false → send dropdown fields only
    // Previous vaccination payloads must never contain any of these keys.
    if (vaccinationType !== 'previous' && !formData.tag_to_session_site) {
      if (formData.lgd_village) {
        payload.village_id = formData.lgd_village;
        payload.village_name = getVillageName(formData.lgd_village, villages);
      }

      if (formData.health_village) {
        payload.health_village_id = formData.health_village;
        payload.health_village_name = getHealthVillageName(
          formData.health_village,
          healthVillages
        );
      }

      if (formData.lgd_ward) {
        payload.ward_id = formData.lgd_ward;
        payload.ward_name = getWardName(formData.lgd_ward, wards);
      }

      if (formData.health_ward) {
        payload.health_ward_id = formData.health_ward;
        payload.health_ward_name = getHealthWardName(formData.health_ward, healthWards);
      }
    }

    // asha_user_id is independent of the location choice — send it whenever
    // it is available, for all types except previous vaccination.
    if (vaccinationType !== 'previous' && formData.asha_user_id) {
      payload.asha_user_id = formData.asha_user_id;
    }

    // Scenario-specific payload fields
    // Each branch delegates materialDetails construction to materialDetailsService
    // so that this function stays focused on payload assembly and API orchestration.
    if (vaccinationType === 'rejection') {
      payload.vaccination_status = false;
      payload.reason = rejectionData?.customReason || rejectionData?.reasonName || 'Vaccine not Available';
      payload.materialDetails = await buildRejectionMaterialDetails(
        rejectionData,
        encryptText
      );
    } else if (vaccinationType === 'current') {
      payload.vaccination_status = true;
      payload.is_past_dated = false;
      // buildCurrentMaterialDetails throws when no vaccine+dose is selected,
      // surfacing a clear validation error rather than posting an empty payload.
      payload.materialDetails = await buildCurrentMaterialDetails(
        formData,
        encryptText
      );
    } else if (vaccinationType === 'previous') {
      payload.vaccination_status = true;
      payload.is_past_dated = true;
      // session_id is part of the shared base payload but must not be sent
      // for previous vaccinations — remove it here.
      delete payload.session_id;
      payload.materialDetails = await buildPreviousMaterialDetails(
        formData,
        encryptText
      );
    }
    console.log('vaccinationApiService - final payload:', payload);

    const response = await uwinAPI.createVaccinationRecord(payload);
    console.log('vaccinationApiService - API response:', response);

    if (response?.success) {
      return {
        success: true,
        data: response.data,
        message:
          response.message ||
          `${vaccinationType} vaccination processed successfully`,
        payload, // Returned for callers that need to inspect the submitted data
      };
    }

    throw new Error(
      response?.message || `Failed to process ${vaccinationType} vaccination`
    );
  } catch (error) {
    console.error(
      `vaccinationApiService.createVaccinationRecord error (${vaccinationType}):`,
      error
    );
    throw error;
  }
};
